import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { subscribeUpgradeRequired } from "@/lib/queryClient";
import { UpgradeModal } from "@/components/upgrade-modal";
import { resolveUpgradeInfo, routeMenuLockKey } from "@/lib/upgrade-info";
import { type AccountType } from "@shared/schema";

/**
 * App-wide listener for centrally-detected 403 `UPGRADE_REQUIRED` responses
 * (Task #4436). When a user reaches a locked menu section via a deep link or a
 * stale tab, its API calls 403; rather than each page showing a raw error toast,
 * this surfaces the shared `UpgradeModal` upgrade CTA naming the locked section
 * and linking to billing.
 *
 * The required upgrade tier comes from the same `/api/app/menu-tier-locks/denied`
 * source the sidebar uses (so the two can't disagree); it is read from cache
 * when already loaded and only fetched on demand when a prompt fires.
 *
 * The modal fires ONLY when the 403's `key` names the menu section the user is
 * currently viewing (`routeMenuLockKey(location) === key`). A background or
 * secondary query that 403s for a DIFFERENT locked section while the user sits
 * on an allowed page (dashboard, properties, tasks, forms, …) must never pop the
 * modal — that was the "upgrade popup appears everywhere for low-tier users"
 * bug. The route key is purely path-based (no dependency on the `/denied` query)
 * so it is already correct during a cold deep-link before the route overlay
 * activates.
 *
 * `suppressed` is true when the host (app-layout) is already showing the
 * route-level tier-lock overlay for the current page, so a deep-link to a
 * known-locked section doesn't double-prompt. It is used two ways:
 *   1. At 403-event time (via a ref) we skip opening the modal when the overlay
 *      already covers the route.
 *   2. Reactively (Task #4444): on a cold deep-link the `/denied` query can
 *      still be loading when the 403 fires, so `suppressed` is briefly false and
 *      the modal opens; once the query resolves and the route overlay turns on,
 *      `suppressed` flips to true — we then auto-dismiss the already-open modal
 *      so only the route overlay remains (no transient double-prompt).
 */
export function UpgradeRequiredListener({
  suppressed = false,
}: {
  suppressed?: boolean;
} = {}) {
  const [location] = useLocation();
  const [state, setState] = useState<{ open: boolean; key: string | null }>({
    open: false,
    key: null,
  });

  const suppressRef = useRef(suppressed);
  suppressRef.current = suppressed;

  // The menu section the current route belongs to (or null). Kept in a ref so
  // the 403-event handler reads the latest value without re-subscribing.
  const routeKeyRef = useRef<string | null>(routeMenuLockKey(location));
  routeKeyRef.current = routeMenuLockKey(location);

  useEffect(() => {
    return subscribeUpgradeRequired(({ key }) => {
      if (suppressRef.current) return;
      // Only prompt for the section the user is actually viewing; ignore 403s
      // from background/secondary queries to other (locked) sections.
      if (!key || routeKeyRef.current !== key) return;
      setState({ open: true, key });
    });
  }, []);

  // When the route-level overlay activates for the current route (e.g. the
  // `/denied` query finally resolved after a cold deep-link), close any reactive
  // modal we already opened so only the route overlay remains.
  useEffect(() => {
    if (suppressed) {
      setState((prev) => (prev.open ? { ...prev, open: false } : prev));
    }
  }, [suppressed]);

  const tierLocksQuery = useQuery<{
    deniedKeys: string[];
    lockedTiers?: Record<string, AccountType>;
  }>({
    queryKey: ["/api/app/menu-tier-locks/denied"],
    staleTime: 60_000,
    // Only fetch on demand; when the sidebar already loaded this, the shared
    // cache serves it immediately. Avoids a stray /api/app call on public pages.
    enabled: state.open,
  });

  const { label, requiredTier, reason } = resolveUpgradeInfo(
    state.key,
    tierLocksQuery.data?.lockedTiers,
  );

  return (
    <UpgradeModal
      open={state.open}
      onOpenChange={(open) => setState((prev) => ({ ...prev, open }))}
      feature={label}
      requiredTier={requiredTier}
      reason={reason}
    />
  );
}
